import { createSelector } from '@ngrx/store';
import { Installer } from '../../domains/users';
import { KudosReason } from '../../domains/users/kudos-reason/kudos-reason.model';
import { allKudosReasons } from '../../domains/users/kudos-reason/kudos-reason.state';
import { KudosReasonsDetails } from './models/detailed-installer-profile-context.model';
import { KudosReasonsElement } from './models/kudos-reasons-element.model';
import { PrimaryKudosInfo } from './models/primary-kudos-info.model';
import { currentInstaller } from './store/profile.selectors';


export const currentInstallerKudosReason = createSelector(
  allKudosReasons,
  currentInstaller,
  (kudosReasons: KudosReason[], installer?: Installer): KudosReason | undefined =>
    installer ? kudosReasons.find(reason => reason.userId === installer.id) : undefined,
);

export type DescribedReason = Omit<KudosReason, 'userId' | 'completedProjectReviews'>;

export type DescribedReasons = Record<keyof DescribedReason, string>;
export const kudosDisplayNames: DescribedReasons = {
  attitudeAverage: 'Attitude',
  punctualityAverage: 'Punctuality',
  qualityAverage: 'Quality',
  professionalismAverage: 'Professionalism',
  dependabilityAverage: 'Dependability',
  timelyCommunicationAverage: 'Timely Communication',
  likelyToUseAgainAverage: 'Likely To Hire Again',
};

export const currentInstallerKudosReasons = createSelector(
  currentInstallerKudosReason,
  (kudosReason?: KudosReason): KudosReasonsElement[] | undefined =>
    kudosReason
      ? Object.keys(kudosReason).reduce(
          (acc: KudosReasonsElement[], key: keyof KudosReason): KudosReasonsElement[] => {
            const value = kudosReason[key];
            if (key !== 'userId' && key !== 'completedProjectReviews' && value !== null && value !== undefined) {
              return [
                ...acc,
                {
                  name: key,
                  count: value as number,
                  description: kudosDisplayNames[key as unknown as keyof DescribedReason] || '',
                },
              ];
            }
            return acc;
          },
          [] as KudosReasonsElement[],
        )
      : undefined,
);

export const maxInstallerKudosReason = createSelector(
  currentInstallerKudosReasons,
  (kudosReasons?: KudosReasonsElement[]): KudosReasonsElement | undefined =>
    kudosReasons
      ? kudosReasons.reduce(
          (acc: KudosReasonsElement | undefined, reason: KudosReasonsElement): KudosReasonsElement | undefined =>
            acc && acc.count > reason.count ? acc : reason,
          undefined,
        )
      : undefined,
);

export const primaryKudosDescriptions: Record<keyof DescribedReason, (name: string) => string> = {
  attitudeAverage: name => `${name} has received the majority of his kudos for his exceptional attitude.`,
  punctualityAverage: name => `${name} has received the majority of his kudos for his punctuality.`,
  qualityAverage: name => `${name} has received the majority of his kudos for his exceptional installation quality.`,
  professionalismAverage: name => `${name} has received the majority of his kudos for his high level of professionalism.`,
  dependabilityAverage: name => `${name} has received the majority of his kudos for his rock solid dependability.`,
  timelyCommunicationAverage: name => `${name} has received the majority of his kudos for his timely communication.`,
  likelyToUseAgainAverage: name => `${name} has received the majority of his kudos for being likely to hire again.`,
};

export const currentInstallerKudosReasonsPrimary = createSelector(
  currentInstallerKudosReasons,
  maxInstallerKudosReason,
  currentInstaller,
  (
    kudosReasons?: KudosReasonsElement[],
    maxKudosReason?: KudosReasonsElement,
    installer?: Installer,
  ): PrimaryKudosInfo | undefined => {
    if ((!maxKudosReason || maxKudosReason.count === 0 || maxKudosReason.count === null) && installer?.firstName) {
      return {
        title: 'No Kudos Yet',
        description: `It doesn't look like ${installer.firstName} has received any kudos yet. When they do, you'll see the breakdown here.`,
      };
    }

    if (kudosReasons && maxKudosReason && maxKudosReason.count > 0 && maxKudosReason.count !== null && installer?.firstName) {
      const maxCount = maxKudosReason.count;
      const maxCountReasons = kudosReasons.filter(reason => reason.count === maxCount);

      if (maxCountReasons.length > 1) {
        return {
          title: 'Well Balanced',
          description: `${installer.firstName} has received kudos in multiple areas. Take a look at the chart for additional detail.`,
        };
      }

      return {
        title: `Exceptional ${kudosDisplayNames[maxKudosReason.name as keyof DescribedReason]}`,
        description:
          primaryKudosDescriptions[maxKudosReason.name as keyof DescribedReason]?.(installer.firstName) ?? '',
      };
    }

    return undefined;
  },
);

export const currentInstallerKudosReasonDetails = createSelector(
  currentInstallerKudosReasons,
  currentInstallerKudosReasonsPrimary,
  (dataPoints?: KudosReasonsElement[], primaryKudos?: PrimaryKudosInfo): KudosReasonsDetails | undefined =>
    dataPoints && primaryKudos
      ? {
          dataPoints,
          primaryKudos,
        }
      : undefined,
);
